import { useEffect, useState, useContext } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { string, func, object } from 'prop-types'
import uuid from 'uuid/v4'
import { CvsAutoComplete } from '@components'
import { accountContext, targetContext } from '@contexts'
import { colors } from '@global/constants'
import { api } from 'api'

const LinkError = styled(ErrorOutlineIcon)`
  margin-left: 10px;
`

const useStyles = makeStyles(theme => ({
  error: {
    color: theme.palette.error.main,
  },
}))

export const GAValidation = ({ type = string, validation = {}, onChange = () => {} }) => {
  const baseOptions = {
    ga_pixel_exists: {
      metadata: {},
      slug: 'ga_pixel_exists',
      uuid: uuid(),
    },
    ga_correct_property: {
      metadata: {},
      slug: 'ga_correct_property',
      uuid: uuid(),
    },
  }

  const getRsSelection = () => {
    let selection
    if (Object.keys(validation.options).length == 0) {
      selection = 'target'
    } else if (
      validation.options.ga_correct_property &&
      Object.keys(validation.options.ga_correct_property.metadata).length == 0
    ) {
      selection = 'target'
    } else if (
      validation.options.ga_correct_property?.metadata.user_provided_id ||
      validation.options.ga_correct_property?.metadata.user_provided_remote_ga_account_id
    ) {
      selection = 'explore'
    } else if (validation.options.ga_correct_property?.metadata.expected_property_id) {
      selection = 'input'
    } else {
      selection = 'target'
    }
    return selection
  }
  const classes = useStyles()

  const { byId, fetchAccounts } = useContext(accountContext)
  const TargetContext = useContext(targetContext)
  const [rsSelection, setRsSelection] = useState(getRsSelection())
  const [gaAccounts, setGaAccounts] = useState([])
  const [reports, setReports] = useState([])

  const vali = {
    ...validation,
    options: {
      ga_pixel_exists: {
        ...baseOptions.ga_pixel_exists,
        ...validation?.options?.ga_pixel_exists,
      },
      ga_correct_property: {
        ...baseOptions.ga_correct_property,
        ...validation?.options?.ga_correct_property,
      },
    },
  }

  const selectedAccount = parseInt(
    vali.options.ga_correct_property.metadata.user_provided_account_id,
  )
  const selectedGAAccount =
    vali.options.ga_correct_property.metadata.user_provided_remote_ga_account_id
  const selectedTarget = parseInt(vali.options.ga_correct_property.metadata.expected_target_id)
  const selectedProperty = vali.options.ga_correct_property.metadata.expected_property_id

  useEffect(() => {
    if (!Object.keys(byId).length) {
      fetchAccounts()
    }
    if (!Object.keys(TargetContext.byId).length) {
      TargetContext.fetchTargets()
    }
    if (selectedAccount) {
      fetchGAAccounts(selectedAccount)
    }
    if (selectedGAAccount) {
      fetchProperties(selectedGAAccount)
    }
  }, [])

  const fetchGAAccounts = account => {
    api.get(`/api/v1/account/connector/${account}/ga-account`).then(res => {
      setGaAccounts(res.data.gaAccounts)
    })
  }

  const fetchProperties = gaAccount => {
    api
      .get(`/api/v1/account/connector/${selectedAccount}/ga-account/${gaAccount}/property/list`)
      .then(res => {
        setReports(res.data.gaProperties)
      })
  }

  const getPropertyIdStatus = () => {
    let status
    switch (rsSelection) {
      case 'target':
        status = !selectedTarget
        break
      case 'explore':
        status = !selectedProperty || !selectedGAAccount || !selectedAccount
        break
      case 'input':
        status = !vali.options.ga_correct_property.metadata.expected_property_id
        break
      default:
        status = false
        break
    }
    return status
  }

  return (
    <div>
      <Grid container style={{ width: 'calc(100% - 35px)' }}>
        <Grid item xs={4}>
          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Checkbox
                checked={vali.options.ga_pixel_exists.enabled}
                onChange={(e, newVal) => {
                  onChange({
                    ...type,
                    ...vali,
                    options: {
                      ...vali.options,
                      ga_pixel_exists: {
                        ...vali.options.ga_pixel_exists,
                        enabled: newVal,
                        metadata: { ga_pixel_exists: true },
                      },
                    },
                  })
                }}
                value={vali.options.ga_pixel_exists.enabled}
                style={{ padding: 2 }}
                color="secondary"
              />
            }
            label={t`Verify pixel exists`}
          />
        </Grid>
        <Grid item xs={8}>
          <Typography
            variant="subtitle1"
            style={{
              color: colors.blue.smokey_light,
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
            }}
          >
            {t`What does this do?`}
          </Typography>
          <Typography variant="body1">
            {t`Enable this option to verify that the links submitted by users fire a Google Analytics Pixel. This helps ensure that URL params trigger the correct Pixels on the page.`}
          </Typography>
        </Grid>
      </Grid>
      <Divider style={{ margin: '20px 0' }} />
      <Grid container style={{ width: 'calc(100% - 35px)' }}>
        <Grid item xs={4}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              style={{ margin: 0 }}
              control={
                <Checkbox
                  checked={vali.options.ga_correct_property.enabled}
                  onChange={(e, newVal) => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        ga_correct_property: {
                          ...vali.options.ga_correct_property,
                          enabled: newVal,
                        },
                      },
                    })
                  }}
                  value={vali.options.ga_correct_property.enabled}
                  style={{ padding: 2 }}
                  color="secondary"
                />
              }
              label="Verify Property ID"
            />
            {vali.options.ga_correct_property.enabled && getPropertyIdStatus() && (
              <LinkError className={classes.error} />
            )}
          </div>
        </Grid>
        <Grid item xs={8}>
          <Typography
            variant="subtitle1"
            style={{
              color: colors.blue.smokey_light,
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
            }}
          >
            {t`What does this do?`}
          </Typography>
          <Typography variant="body1">
            <Trans>
              Enabling this option will ensure that a Google Analytics Pixel is firing and is
              sending data to the correct Property. In the case of multiple Pixels on a single page,
              Claravine will check to ensure that at least one Pixel sends data to the correct
              Property.
            </Trans>
          </Typography>
          <div>
            <RadioGroup
              name="reportSuite"
              value={rsSelection}
              onChange={(e, val) => {
                setRsSelection(val)
                onChange({
                  ...vali,
                  options: {
                    ...vali.options,
                    ga_correct_property: {
                      ...vali.options.ga_correct_property,
                      metadata: {},
                    },
                  },
                })
              }}
            >
              <FormControlLabel
                value="target"
                control={<Radio />}
                label={t`Inherit through a target`}
              />
              <FormControlLabel value="explore" control={<Radio />} label={t`Explore properties`} />
              <FormControlLabel value="input" control={<Radio />} label={t`Input by hand`} />
            </RadioGroup>
          </div>
          <div>
            {rsSelection === 'target' && (
              <div>
                <CvsAutoComplete
                  style={{ marginBottom: 0 }}
                  label={t`Claravine target`}
                  options={Object.values(TargetContext.byId)
                    .filter(t => t.connector.connector_type_id == 2)
                    .map(t => {
                      return {
                        value: t.id,
                        label: t.name,
                      }
                    })}
                  onSelected={item => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        ga_correct_property: {
                          ...vali.options.ga_correct_property,
                          metadata: {
                            expected_target_id: item,
                          },
                        },
                      },
                    })
                  }}
                  selected={selectedTarget}
                />
                {vali.options.ga_correct_property.enabled && !selectedTarget && (
                  <span className={classes.error}>{t`*Required`}</span>
                )}
              </div>
            )}
            {rsSelection === 'explore' && (
              <div>
                <CvsAutoComplete
                  style={{ marginBottom: 0 }}
                  label={t`GA account`}
                  options={Object.values(byId)
                    .filter(t => t.connector_type_id == 2)
                    .map(t => {
                      return {
                        value: t.id,
                        label: t.name,
                      }
                    })}
                  onSelected={item => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        ga_correct_property: {
                          ...vali.options.ga_correct_property,
                          metadata: {
                            ...vali.options.ga_correct_property?.metadata,
                            user_provided_account_id: item,
                            expected_target_id: null,
                          },
                        },
                      },
                    })
                    fetchGAAccounts(item)
                  }}
                  selected={selectedAccount}
                />
                {vali.options.ga_correct_property.enabled && !selectedAccount && (
                  <span className={classes.error}>{t`*Required`}</span>
                )}
                <CvsAutoComplete
                  style={{ marginBottom: 0 }}
                  label={t`Google Analytics account`}
                  options={gaAccounts.map(t => {
                    return {
                      value: t.ga_account_id,
                      label: t.ga_account_name,
                    }
                  })}
                  onSelected={item => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        ga_correct_property: {
                          ...vali.options.ga_correct_property,
                          metadata: {
                            ...vali.options.ga_correct_property?.metadata,
                            user_provided_remote_ga_account_id: item,
                            expected_target_id: null,
                          },
                        },
                      },
                    })
                    fetchProperties(item)
                  }}
                  selected={selectedGAAccount}
                  showSpinner={selectedAccount && !gaAccounts.length}
                />
                {vali.options.ga_correct_property.enabled && !selectedGAAccount && (
                  <span className={classes.error}>{t`*Required`}</span>
                )}
                <CvsAutoComplete
                  style={{ marginBottom: 0 }}
                  label={t`GA property`}
                  options={reports.map(t => {
                    return {
                      value: t.ga_property_id,
                      label: t.ga_property_name,
                    }
                  })}
                  onSelected={item => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        ga_correct_property: {
                          ...vali.options.ga_correct_property,
                          metadata: {
                            ...vali.options.ga_correct_property?.metadata,
                            expected_property_id: item,
                            expected_target_id: null,
                          },
                        },
                      },
                    })
                  }}
                  selected={selectedProperty}
                  showSpinner={selectedGAAccount && !reports.length}
                />
                {vali.options.ga_correct_property.enabled && !selectedProperty && (
                  <span className={classes.error}>{t`*Required`}</span>
                )}
              </div>
            )}
            {rsSelection === 'input' && (
              <div>
                <TextField
                  variant="outlined"
                  label={t`Property ID`}
                  placeholder="UA-XXXXXXXX-X  "
                  fullWidth
                  value={vali.options.ga_correct_property.metadata.expected_property_id}
                  onChange={e => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        ga_correct_property: {
                          ...vali.options.ga_correct_property,
                          metadata: {
                            expected_property_id: e.target.value,
                          },
                        },
                      },
                    })
                  }}
                />
                {vali.options.ga_correct_property.enabled &&
                  !vali.options.ga_correct_property.metadata.expected_property_id && (
                    <span className={classes.error}>{t`*Required`}</span>
                  )}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

GAValidation.propTypes = {
  validation: object,
  type: string,
  onChange: func,
}
